/* eslint-disable @typescript-eslint/naming-convention */
import { verts_x, verts_base, indices_base, indices_x } from './assets/models';

const vertexShader = `
  attribute highp vec3 pos;
  attribute highp vec3 norm;

  uniform highp mat4 M;
  varying highp vec3 _norm;
  varying highp vec3 _pos;

  void main(void)
  {
    gl_Position = M * vec4(pos, 1.0);
    _norm = normalize((M * vec4(norm, 1.0) - M * vec4(0.0, 0.0, 0.0, 1.0)).xyz);
    _pos = gl_Position.xyz;
  }
`;

const fragmentShader = `
  uniform highp vec3 color;
  varying highp vec3 _norm;
  varying highp vec3 _pos;

  highp float light(highp vec3 v)
  {
    return clamp(dot(_norm, normalize(v - _pos)), 0.0, 1.0);
  }
  
  void main(void)
  {
    highp float key = light(vec3(-0.6, 0.6, -2.0));
    highp float fill = light(vec3(5.0, -3.0, 0.0));
    highp float back = light(vec3(3.0, 0.0, 2.0));

    gl_FragColor = vec4(color * (key + 0.3 * fill + 0.5 * back), 1.0);
  }
`;

function get_mesh(verts, indices, color, gl) {
  const mesh = { count: indices.length };

  var vs = new Float32Array(indices.length * 6);

  for (var i = 0; i < indices.length; i += 3) {
    // Find normal with cross product
    var a = glm.vec3(verts[indices[i + 0] * 3], verts[indices[i + 0] * 3 + 1], verts[indices[i + 0] * 3 + 2]);
    var b = glm.vec3(verts[indices[i + 1] * 3], verts[indices[i + 1] * 3 + 1], verts[indices[i + 1] * 3 + 2]);
    var c = glm.vec3(verts[indices[i + 2] * 3], verts[indices[i + 2] * 3 + 1], verts[indices[i + 2] * 3 + 2]);

    var n = glm.normalize(glm.cross(a['-'](b), a['-'](c)));

    // Iterate over three corners of the triangle
    for (var j = 0; j < 3; ++j) {
      // Copy vertex location and normal
      for (var k = 0; k < 3; ++k) {
        vs[6 * (i + j) + k] = verts[indices[i + j] * 3 + k];
        vs[6 * (i + j) + 3 + k] = n[k];
      }
    }
  }

  mesh.verts = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, mesh.verts);
  gl.bufferData(gl.ARRAY_BUFFER, vs, gl.STATIC_DRAW);

  mesh.color = new Float32Array(color);
  return mesh;
}

function draw_mesh(mesh, shader, gl) {
  gl.bindBuffer(gl.ARRAY_BUFFER, mesh.verts);
  gl.vertexAttribPointer(shader.pos, 3, gl.FLOAT, false, 24, 0);
  gl.vertexAttribPointer(shader.norm, 3, gl.FLOAT, false, 24, 12);

  gl.uniform3fv(shader.color, mesh.color);

  gl.drawArrays(gl.TRIANGLES, 0, mesh.count);
}

class Cube {
  width = 200;

  height = 200;

  env = {
    down: false,
    yaw: glm.pi / 2,
    pitch: -glm.pi,

    M: function (env) {
      let m = glm.mat4();
      //m = glm.scale(m, glm.vec3(0.5, 1, 1));
      m = glm.scale(m, glm.vec3(0.5, 0.5, 0.5));

      m = glm.rotate(m, env.pitch, glm.vec3(1, 0, 0));
      m = glm.rotate(m, env.yaw, glm.vec3(0, 1, 0));

      return m;
    },

    N: function (env) {
      let m = glm.mat4();

      m = glm.rotate(m, env.pitch, glm.vec3(1, 0, 0));
      m = glm.rotate(m, env.yaw, glm.vec3(0, 1, 0));
      m = glm.rotate(m, glm.pi / 2, glm.vec3(0, 1, 0));

      return m;
    },
  };

  constructor(parent, setter, yaw = 0, pitch = 0) {
    this.canvas = document.createElement('canvas');
    this.parent = parent;
    this.setter = setter;

    this.env.yaw = yaw;
    this.env.pitch = pitch;

    parent.appendChild(this.canvas);

    this.createProgram();
    this.addEventListeners();

    this.draw();
  }

  destroy() {
    this.parent.removeChild(this.canvas);
  }

  createMeshes() {
    this.x = get_mesh(verts_x, indices_x, [0.8, 0.8, 0.8], this.gl);
    this.base = get_mesh(verts_base, indices_base, [0.5, 0.5, 0.5], this.gl);
  }

  createProgram() {
    this.gl = this.canvas.getContext('webgl');

    // set the display size of the canvas.
    var devicePixelRatio = window.devicePixelRatio || 1;
    this.canvas.style.width = this.width + 'px';
    this.canvas.style.height = this.height + 'px';

    // set the size of the drawingBuffer
    this.canvas.width = this.width * devicePixelRatio;
    this.canvas.height = this.height * devicePixelRatio;
    this.gl.viewport(0, 0, this.width * devicePixelRatio, this.height * devicePixelRatio);

    this.createMeshes();

    const frag_src = fragmentShader;
    const frag_shader = this.gl.createShader(this.gl.FRAGMENT_SHADER);
    this.gl.shaderSource(frag_shader, frag_src);
    this.gl.compileShader(frag_shader);

    const vert_src = vertexShader;
    const vert_shader = this.gl.createShader(this.gl.VERTEX_SHADER);
    this.gl.shaderSource(vert_shader, vert_src);
    this.gl.compileShader(vert_shader);

    if (!this.gl.getShaderParameter(vert_shader, this.gl.COMPILE_STATUS)) {
      alert('An error occurred compiling the shaders: ' + this.gl.getShaderInfoLog(vert_shader));
      return null;
    }

    if (!this.gl.getShaderParameter(frag_shader, this.gl.COMPILE_STATUS)) {
      alert('An error occurred compiling the shaders: ' + this.gl.getShaderInfoLog(frag_shader));
      return null;
    }

    this.shader = this.gl.createProgram();
    this.gl.attachShader(this.shader, vert_shader);
    this.gl.attachShader(this.shader, frag_shader);
    this.gl.linkProgram(this.shader);

    this.gl.useProgram(this.shader);
    this.shader.pos = this.gl.getAttribLocation(this.shader, 'pos');
    this.shader.M = this.gl.getUniformLocation(this.shader, 'M');
    this.shader.color = this.gl.getUniformLocation(this.shader, 'color');
    this.shader.norm = this.gl.getAttribLocation(this.shader, 'norm');

    this.gl.enable(this.gl.DEPTH_TEST);
    this.gl.depthFunc(this.gl.LESS);
  }

  addEventListeners() {
    this.boundMove = this.boundMove.bind(this);
    this.down = this.down.bind(this);
    this.up = this.up.bind(this);

    this.canvas.addEventListener(
      'pointerdown',
      (event) => {
        event.preventDefault();
        event.target.setPointerCapture(event.pointerId);

        this.down(event);

        window.addEventListener('pointermove', this.boundMove);
      },
      false
    );

    this.canvas.addEventListener('pointercancel', (event) => {
      this.up(event);

      window.removeEventListener('pointermove', this.boundMove);
    });

    this.canvas.addEventListener(
      'pointerup',
      (event) => {
        this.up(event);

        window.removeEventListener('pointermove', this.boundMove);
      },
      false
    );
  }

  down(event) {
    this.env.down = true;
    this.env.x = event.clientX;
    this.env.y = event.clientY;
  }

  up() {
    this.env.down = false;
  }

  boundMove(event) {
    event.preventDefault();

    this.move(event);
  }

  move(event) {
    if (this.env.down) {
      this.env.yaw += (this.env.x - event.clientX) / 100.0;
      this.env.pitch += (this.env.y - event.clientY) / 100.0;
      this.env.x = event.clientX;
      this.env.y = event.clientY;

      this.env.yaw = glm.clamp(this.env.yaw, -0.5 * glm.pi, 0.5 * glm.pi);
      this.env.pitch = glm.clamp(this.env.pitch, -0.5 * glm.pi, 0.5 * glm.pi);

      this.draw();

      this.callSetter();
    }
  }

  draw() {
    const gl = this.gl;

    requestAnimationFrame(() => {
      gl.clearColor(0, 0, 0, 0);
      gl.clearDepth(1.0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      gl.useProgram(this.shader);

      const drawMatrix = this.env.M(this.env);

      gl.enableVertexAttribArray(this.shader.pos);
      gl.enableVertexAttribArray(this.shader.norm);
      gl.uniformMatrix4fv(this.shader.M, false, drawMatrix.elements);

      gl.clear(gl.DEPTH_BUFFER_BIT);
      [this.base, this.x].forEach((mesh) => {
        draw_mesh(mesh, this.shader, gl);
      });
    });
  }

  callSetter() {
    const matrix = this.env.N(this.env);
    const x = glm.vec4(1, 0, 0, 0);

    const v = matrix['*'](x);

    const rx = glm.normalize(v);
    const ve = rx.rgb.elements;
    const directions = [ve[0], ve[1], Math.abs(ve[2])];

    this.setter(directions, this.env.yaw, this.env.pitch);
  }

  setNewYawAndPitch(yaw, pitch) {
    this.env.yaw = yaw;
    this.env.pitch = pitch;

    this.draw();
  }
}

export default Cube;
